import React, { useState, useEffect } from 'react';
import { PersonIcon } from './icons';
import { avatarUrl } from '../services/api';
import './Avatar.css';

// Deterministic, pleasant palette for initial-based placeholders.
const COLORS = [
  '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#b02a37',
  '#fd7e14', '#198754', '#20c997', '#0aa2c0', '#4a6fa5',
];

function colorFor(key) {
  const s = String(key || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return COLORS[h % COLORS.length];
}

// Handles "Last, First", "First Last", or a single token (e.g. a username).
function initials(name) {
  if (!name) return '';
  let first = '';
  let last = '';
  if (name.includes(',')) {
    const parts = name.split(',').map((s) => s.trim());
    last = parts[0] || '';
    first = parts[1] || '';
  } else {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    first = parts[0] || '';
    last = parts.length > 1 ? parts[parts.length - 1] : '';
  }
  if (first && last) return (first[0] + last[0]).toUpperCase();
  return (first || last || '').slice(0, 2).toUpperCase();
}

/**
 * User avatar. Loads the user's photo (by userId, or an explicit imageUrl) and
 * falls back to a generic placeholder — initials on a name-derived color, or a
 * person icon when there's no name — if none is configured. `version` busts the
 * image cache after a change.
 */
function Avatar({ userId, name, imageUrl, version, size = 28, title }) {
  const [failed, setFailed] = useState(false);
  const src = imageUrl || avatarUrl(userId, version);

  useEffect(() => { setFailed(false); }, [src]);

  const label = name || 'Unknown';
  const dim = { width: size, height: size };

  if (src && !failed) {
    return (
      <img
        className="avatar avatar-img"
        style={dim}
        src={src}
        alt={label}
        title={title || label}
        onError={() => setFailed(true)}
      />
    );
  }
  const init = initials(name);
  if (!init) {
    return (
      <span className="avatar avatar-generic" style={dim} title={title || label} aria-label={label}>
        <PersonIcon size={Math.round(size * 0.6)} />
      </span>
    );
  }
  return (
    <span
      className="avatar avatar-initials"
      style={{ ...dim, background: colorFor(name), fontSize: Math.round(size * 0.4) }}
      title={title || label}
      aria-label={label}
    >
      {init}
    </span>
  );
}

export default Avatar;
